import { AfterViewInit, Component, ElementRef, Input, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { ApiGiftItem, ApiGiftReceipt, ApiPostGifts, ApiUser, GiftsApi } from '@tytapp/api';
import { FormComponent, FormHandlingComponent, PageComponent } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { environment } from 'environments/environment';
import { CartItem, CartService } from '../cart.service';
import { CreditCardSelectorComponent } from '../credit-card-selector/credit-card-selector.component';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface GiftItemData extends ApiGiftItem {
  price: number;
  charge_type: string;
}

export interface SponsorshipItem extends CartItem {
  recipientEmail: string;
  recipientName: string;
  productSKU: string;
  purchaserName: string;
  months?: number;
  deliverImmediately: boolean;
  deliverAt?: string;
  sponsorship: boolean;
}

@Component({
  selector: 'app-sponsorships',
  templateUrl: './sponsorships.component.html',
  styleUrls: ['./sponsorships.component.scss']
})
export class SponsorshipsComponent extends PageComponent implements FormHandlingComponent, AfterViewInit {
  private giftsApi = inject(GiftsApi);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private cartService = inject(CartService);
  private snackBar = inject(MatSnackBar);

  cart = this.cartService.getCart<SponsorshipItem>('sponsorships');

  @ViewChild('redemptionLinkInput') redemptionLinkInput: ElementRef<HTMLInputElement>;
  @ViewChild('form') form: FormComponent;
  @ViewChild('ccElement') ccElement: CreditCardSelectorComponent;

  sponsorshipItemUuid = "";
  user: ApiUser;
  submitting = false;
  editCart = false;
  step = 'one';
  validityError = false;
  selectProduct = false;
  selectQuantity = false;
  itemUnderEdit: SponsorshipItem;
  sponsorshipItems: ApiGiftItem[];

  async init() {
    this.itemUnderEdit = this.newItem();
    this.step = 'one';

    let routeParams: ParamMap;

    let updateRoute = async () => {

      if (!routeParams)
        return;

      await this.cartService.ready;

      let step = routeParams.get('step');
      let item = routeParams.get('item');

      this.itemUnderEdit = this.newItem();
      if (step == 'cart') {
        this.step = 'two';
        if (this.cart.items.length === 0)
          this.router.navigate(['/sponsorship/add'], { replaceUrl: true });
      } else if (step == 'add') {
        this.addAnotherSponsorship();
      } else if (step == 'edit') {
        this.editSponsorshipItem(this.cart.items.find(x => x.uuid == item));
      } else if (step == 'complete' && this.sponsorshipItems) {
        this.step = 'three';
      } else {
        if (this.cart.items.length == 0)
          this.router.navigate([`/sponsorship/add`], { replaceUrl: true });
        else
          this.router.navigate([`/sponsorship/cart`], { replaceUrl: true });
      }
    }

    this.route.paramMap.subscribe(async params => {
      routeParams = params;
      updateRoute();
    });

    this.subscribe(this.userService.userChanged, async user => {
      this.user = user;

      await this.cartService.ready;

      updateRoute();
    });

    if (this.cart.items.length) {
      this.step = 'two';
    }
  }

  simulatePurchase() {
    this.step = 'three';
    this.sponsorshipItems = [{
      code: 'ABCDEF',
      product_sku: 'insider_monthly',
      redemption_url: 'https://accounts.staging.tyt.com/gift/redemption/ABCDEF',
      purchased_quantity: 10
    }]
  }

  get appUrl() {
    return environment.urls.app;
  }

  resetFields() {
    this.itemUnderEdit = this.newItem();
    this.selectProduct = false;
    this.selectQuantity = false;
  }

  sponsorshipProductName(sponsorshipItem: SponsorshipItem) {
    let options = sponsorshipItem.productSKU.split('-');
    if (!sponsorshipItem.recurs) {
      return `${this.capitalizeFirstLetter(options[0])} ${sponsorshipItem.months} months`
    } else {
      return `${this.capitalizeFirstLetter(options[0])} ${options[1] === 'annually' ? 'annual' : 'monthly'}`
    }
  }

  itemUnderEditName(itemUnderEdit: SponsorshipItem) {
    if (!itemUnderEdit.productSKU) {
      return '';
    }
    let options = itemUnderEdit.productSKU.split('-');
    if (options[1] === 'monthly') {
      return `${options[1]} ${this.capitalizeFirstLetter(options[0])}`
    } else {
      return `${this.capitalizeFirstLetter(options[0])} ${options[2]} months`
    }
  }

  receiptProductName(sponsorshipItem: ApiGiftItem) {
    let options = sponsorshipItem.product_sku.split('_');
    if (sponsorshipItem.months) {
      return `${this.capitalizeFirstLetter(options[0])} ${sponsorshipItem.months} months`
    } else {
      return `${this.capitalizeFirstLetter(options[0])} ${options[1]}`
    }

  }

  ngAfterViewInit() {
    // this.ccElement.getSource();
  }

  newItem() {
    return this.itemUnderEdit = this.cartService.newItem('sponsorship', {
      purchaserName: this.user?.display_name ?? '',
      deliverImmediately: true,
      price: 0,
      recipientEmail: '',
      recipientName: '',
      productSKU: '',
      sponsorship: true,
      quantity: null
    });
  }

  cloneGiftItem(item: SponsorshipItem): SponsorshipItem {
    return { ...item };
  }

  addAnotherSponsorship() {
    this.step = 'one'
    this.resetFields();
  }

  editSponsorshipItem(sponsorshipItem) {
    this.itemUnderEdit = this.cloneGiftItem(sponsorshipItem);

    if (!this.itemUnderEdit.recurs) {
      let type = this.itemUnderEdit.productSKU.replace('-monthly', '');
      this.itemUnderEdit.productSKU = `${type}-fixed-${this.itemUnderEdit.months}`;
    }

    this.editCart = true;
    this.step = 'one';
  }

  removeGiftItem(item) {
    this.cartService.removeItem(this.cart, item);

    if (this.cart.items.length === 0) {
      this.step = 'one';
      this.router.navigate(['/sponsorship/add']);
    }
  }

  async addToCart(updateItem = false) {

    if (!this.itemUnderEdit.productSKU) {
      this.selectProduct = true;
    }

    if (this.itemUnderEdit.quantity < 5) {
      this.selectQuantity = true;
    }

    if (this.selectProduct || this.selectQuantity)
      return;

    this.itemUnderEdit.quantity = Math.floor(this.itemUnderEdit.quantity);

    let item: SponsorshipItem = this.cloneGiftItem(this.itemUnderEdit);
    let options = item.productSKU.split('-');
    if (options[1] === 'fixed') {
      // Fixed
      item.productSKU = `${options[0]}-monthly`;
      item.recurs = undefined;
      item.months = Number(options[2]);
    } else {
      // Renewing
      item.recurs = <'monthly' | 'annually'>options[1];
      item.months = 0;
    }

    item.sponsorship = true;

    this.cartService.updateItem(this.cart, item);
    // this.resetFields();

    this.step = 'two';

    this.router.navigate(['/sponsorship/cart']);
  }

  submit() {

  }

  selectMembershipOption(event: Event, price: number) {
    this.itemUnderEdit.productSKU = (event.target as HTMLElement).id;
    this.itemUnderEdit.price = price;
    this.selectProduct = false;
  }

  async proceedToPayment() {
    // CRITICAL, DO NOT DO ASYNC HERE
    if (this.submitting)
      return;
    this.submitting = true;
    // END CRITICAL

    if (!this.ccElement.isValid()) {
      this.submitting = false;
      return;
    }

    let cardSource = await this.ccElement.getSource();
    if (!cardSource) {
      this.submitting = false;
      return;
    }

    try {
      let purchaseResponse = await this.giftsApi.purchase({
        purchaser_email: this.cart.email,
        gateway_name: 'stripe',
        gateway_data: JSON.stringify({ token: cardSource }),
        gift_items: this.cart.items.map(cartItem => <ApiGiftItem>{
          product_sku: cartItem.productSKU.replace(/-/g, '_'),
          recipient_name: cartItem.recipientName,
          recipient_email: cartItem.recipientEmail,
          purchaser_name: cartItem.purchaserName,
          purchaser_email: this.user?.email,
          recurring: cartItem.recurs ? true : false,
          sponsorship: true,
          purchased_quantity: cartItem.quantity || 1,
          deliver_at: cartItem.deliverImmediately ? undefined : cartItem.deliverAt,
          months: cartItem.months,
        })
      }).toPromise();

      this.cartService.clear(this.cart);
      this.sponsorshipItems = purchaseResponse.gift_items.map((item) => {
        return item;
      });
      this.submitting = false;
      this.router.navigate(['/sponsorship/complete']);
    } catch (e) {
      let response = e.json();

      console.error("Error occurred while performing gift purchase request")
      console.error(e);

      if (response.error == 'invalid') {
        this.validityError = true;
      } else if (response.error == 'contact-support') {
        this.showError("An error occurred while processing this request. Please contact support at support@tytnetwork.com for further assistance.");
      } else if (response.error == 'failure') {
        this.showError("An error occurred while processing this request. Your card was not charged. Please try again later.");
      } else if (response.code == 'invalid-order') {
        this.showError("Uh oh, a problem occurred submitting your request. Please try again later or contact TYT Support (code: invalid_order).")
      } else {
        this.showError(response.message || response.error || "An error has occurred");
      }
      this.submitting = false;
    }
  }

  get newCardMessage() {
    if (this.cart.items.some(x => x.recurs)) {
      return `Because you are purchasing a renewable gift, `
        + `this card will be used for renewing your own membership in `
        + `addition to being used to renew your gift memberships.`
        ;
    } else {
      return null;
    }
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  highlightRedemptionLink() {
    this.redemptionLinkInput.nativeElement.select();
  }

  copyRedemptionLink() {
    this.highlightRedemptionLink();
    document.execCommand('copy');
    this.snackBar.open('Copied to clipboard.', undefined, { duration: 3000 });
  }
}
